import { useState } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { useAHUControl } from '@/contexts/ahu-control-context';
import { calculateTimeAgo } from '@/utils/constants';

const TEMPERATURE_STEP = 1;
const TEMPERATURE_MIN = 16;
const TEMPERATURE_MAX = 30;

interface TemperatureControlProps {
  returnTemp: number | undefined;
  onChange: (value: number) => void;
  lastUpdated: any
}

interface SmallTemperatureControlProps {
  onChange: (value: number) => void;
}

export function TemperatureControl({ returnTemp, lastUpdated, onChange }: TemperatureControlProps) {
  const { state: { setpoint }, setSetpoint } = useAHUControl();
  const [time, setTime] = useState<NodeJS.Timeout | undefined>();

  const handleSetpointChange = (value: number) => {
    setSetpoint(Number(value.toFixed(1)) || undefined);
    clearTimeout(time)
    setTime(setTimeout(function () {
      if (value >= TEMPERATURE_MIN && value <= TEMPERATURE_MAX) {
        onChange(Number(value.toFixed(1)))
      }
    }, 500))
  };

  const handleBlur = () => {
    if (setpoint === undefined) return;

    const newValue = Math.min(Math.max(setpoint, TEMPERATURE_MIN), TEMPERATURE_MAX);
    setTimeout(function () {
      setSetpoint(Number(newValue.toFixed(1)));
      if (Number(newValue.toFixed(1)) !== setpoint) {
        onChange(Number(newValue.toFixed(1)))
      }
    }, 250)
  }

  return (
    <div className="grid grid-cols-2 gap-3">
      {/* Set Temperature */}
      <div className="bg-bg rounded-lg border border-light-grey p-2">
        <div className="text-dark-blue heading-7 mb-1.5">Set Temp °C</div>
        <div className="relative w-24">
          <input
            type="number"
            min={TEMPERATURE_MIN}
            max={TEMPERATURE_MAX}
            value={setpoint !== undefined ? setpoint : ''}
            placeholder="-"
            onBlur={handleBlur}
            onChange={(e) => handleSetpointChange(Number(e.target.value))}
            className="w-full h-8 px-3 py-1 text-[13px] border border-light-grey rounded-lg focus:outline-none focus:ring-1 focus:ring-primary text-center"
          />
          <div className="absolute right-1 top-0 bottom-0 flex flex-col justify-center gap-0.5">
            <button
              onClick={() => {
                if (setpoint === undefined) {
                  handleSetpointChange(TEMPERATURE_MIN);
                } else {
                  handleSetpointChange(Math.min(setpoint + TEMPERATURE_STEP, TEMPERATURE_MAX));
                }
              }}
              className="h-3 w-6 flex items-center justify-center hover:bg-bg/50 transition-colors"
            >
              <ChevronUp className="h-2.5 w-2.5 text-primary" />
            </button>
            <button
              onClick={() => {
                if (setpoint === undefined) {
                  handleSetpointChange(TEMPERATURE_MIN);
                } else {
                  handleSetpointChange(Math.max(setpoint - TEMPERATURE_STEP, TEMPERATURE_MIN));
                }
              }}
              className="h-3 w-6 flex items-center justify-center hover:bg-bg/50 transition-colors"
            >
              <ChevronDown className="h-2.5 w-2.5 text-primary" />
            </button>
          </div>
        </div>
      </div>

      {/* Return Temperature */}
      <div className="bg-bg rounded-lg border border-light-grey p-2">
        <div className="text-dark-blue heading-7 mb-1.5">Return Temp °C</div>
        <div className="flex items-center">
          <input
            type="text"
            value={returnTemp !== undefined && returnTemp !== null ? returnTemp : '-'}
            disabled
            className="w-24 h-8 px-3 py-1 text-[13px] bg-light-grey border border-light-grey rounded-lg cursor-not-allowed text-center"
          />
          <span className="paragraph-10 text-dark-grey/70 ml-2">{calculateTimeAgo(lastUpdated)}</span>
        </div>
      </div>
    </div>
  );
}

export function SmallTemperatureControl({ onChange }: SmallTemperatureControlProps) {
  const { state: { setpoint, mode }, setSetpoint } = useAHUControl();
  const [time, setTime] = useState<NodeJS.Timeout | undefined>();
  const isDisabled = mode !== 'cool';

  const handleSetpointChange = (value: number) => {
    if (isDisabled) return; // Don't allow changes when disabled
    setSetpoint(Number(value.toFixed(1)) || undefined);
    clearTimeout(time)
    setTime(setTimeout(function () {
      if (value >= TEMPERATURE_MIN && value <= TEMPERATURE_MAX) {
        onChange(Number(value.toFixed(1)))
      }
    }, 500))
  };

  const handleBlur = () => {
    if (setpoint === undefined || isDisabled) return;

    const newValue = Math.min(Math.max(setpoint, TEMPERATURE_MIN), TEMPERATURE_MAX);
    setTimeout(function () {
      setSetpoint(Number(newValue.toFixed(1)));
      if (Number(newValue.toFixed(1)) !== setpoint) {
        onChange(Number(newValue.toFixed(1)))
      }
    }, 250)
  }

  return (
    <div className="bg-bg rounded-lg border border-light-grey p-2">
      <div className="text-dark-blue heading-7 mb-1.5">Set Temp °C</div>
      <div className="relative w-28">
        <input
          type="number"
          min={TEMPERATURE_MIN}
          max={TEMPERATURE_MAX}
          value={setpoint !== undefined ? setpoint : ''}
          placeholder="-"
          disabled={isDisabled}
          onBlur={handleBlur}
          onChange={(e) => handleSetpointChange(Number(e.target.value))}
          className={`w-full h-7 px-3 py-0.5 text-[13px] border border-light-grey rounded-lg focus:outline-none focus:ring-1 focus:ring-primary text-center ${
            isDisabled ? 'bg-light-grey cursor-not-allowed opacity-50' : ''
          }`}
        />
        <div className="absolute right-1 top-0 bottom-0 flex flex-col justify-center gap-0.5">
          <button
            disabled={isDisabled}
            onClick={() => {
              if (!isDisabled) {
                if (setpoint === undefined) {
                  handleSetpointChange(TEMPERATURE_MIN);
                } else {
                  handleSetpointChange(Math.min(setpoint + TEMPERATURE_STEP, TEMPERATURE_MAX));
                }
              }
            }}
            className={`h-3 w-5 flex items-center justify-center transition-colors ${
              isDisabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-bg/50'
            }`}
          >
            <ChevronUp className="h-2 w-2 text-primary" />
          </button>
          <button
            disabled={isDisabled}
            onClick={() => {
              if (!isDisabled) {
                if (setpoint === undefined) {
                  handleSetpointChange(TEMPERATURE_MIN);
                } else {
                  handleSetpointChange(Math.max(setpoint - TEMPERATURE_STEP, TEMPERATURE_MIN));
                }
              }
            }}
            className={`h-3 w-5 flex items-center justify-center transition-colors ${
              isDisabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-bg/50'
            }`}
          >
            <ChevronDown className="h-2 w-2 text-primary" />
          </button>
        </div>
      </div>
    </div>
  );
}